import React, { useEffect, useMemo, useState } from "react";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import Dropdown from "src/components/ui/Dropdown";
import { Badge } from "src/components/ui/Badge";
import Modal from "src/components/ui/Modal";
import MailContent from "src/components/common/MailContent";

import { toast } from "react-toastify";
import { formatDateTime } from "src/utils/date-time";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supportRequestActions from "src/redux/slices/supportRequestSlice";

const SupportRequestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list, updateStatus } = useAppSelector(
    (state) => state.supportRequest
  );
  const [limit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, string>
  >({});
  const [pendingStatusId, setPendingStatusId] = useState<string | null>(null);

  // Modal state for viewing a support request in an email-like view
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const handleOpenRequestModal = (row: any) => {
    setSelectedRequest(row);
    setOpenRequestModal(true);
  };

  const handleCloseRequestModal = () => {
    setOpenRequestModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    dispatch(
      supportRequestActions.getSupportRequestsListTenantRequest({
        page: currentPage,
        limit,
      })
    );
  }, [currentPage, dispatch, limit]);

  useEffect(() => {
    if (updateStatus.data && pendingStatusId) {
      toast.success("Support request status updated.");
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[pendingStatusId];
        return next;
      });
      setPendingStatusId(null);
      dispatch(supportRequestActions.resetUpdateSupportRequestStatusAdmin());
      dispatch(
        supportRequestActions.getSupportRequestsListTenantRequest({
          page: currentPage,
          limit,
        })
      );
    } else if (updateStatus.error && pendingStatusId) {
      toast.error(`Error: ${updateStatus.error}`);
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[pendingStatusId];
        return next;
      });
      setPendingStatusId(null);
      dispatch(supportRequestActions.resetUpdateSupportRequestStatusAdmin());
    }
  }, [currentPage, dispatch, limit, pendingStatusId, updateStatus]);

  const statusOptions = useMemo(
    () => [
      { label: "Open", value: "OPEN" },
      { label: "Closed", value: "CLOSED" },
    ],
    []
  );

  const normalizeStatus = (status: string) =>
    status?.toString().trim().toLowerCase();

  // const handleStatusChange = (row: any, nextStatus: string) => {
  //   const rowId = row?._id || row?.id;
  //   if (!rowId) return;
  //   const currentStatus = normalizeStatus(
  //     row?.status || row?.requestStatus || row?.supportStatus || ""
  //   );
  //   if (!nextStatus || nextStatus === currentStatus) return;

  //   setStatusOverrides((prev) => ({ ...prev, [rowId]: nextStatus }));
  //   setPendingStatusId(rowId);
  //   dispatch(
  //     supportRequestActions.updateSupportRequestStatusAdminRequest({
  //       _id: rowId,
  //       status: nextStatus,
  //     })
  //   );
  // };

  const raiseRequest = () => {
    console.log("Raise Support Request clicked");
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Support Requests | Invyntra",
        description: "Track and resolve all support requests in one place.",
        keywords: "support, requests, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page Header */}
        <PageHeader
          title="Support Requests"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Support", path: "/app/support" },
          ]}
        />

        <Card>
          <CardHeader
            title="Support Requests"
            description="Manage and resolve support requests from users"
            actions={<Button onClick={raiseRequest}>Raise Request</Button>}
          />
          <TableV2
            columns={[
              {
                key: "requestId",
                header: "Request ID",
                accessor: ({ key, row }) => (
                  <button
                    className="font-mono text-indigo-600 hover:underline"
                    onClick={() => handleOpenRequestModal(row)}
                  >
                    #{row[key]}
                  </button>
                ),
              },
              { key: "name", header: "Name" },
              { key: "email", header: "Email" },
              { key: "mobile", header: "Mobile" },
              {
                key: "message",
                header: "Message",
                accessor: ({ key, row }) => (
                  <div className="max-w-xs truncate">{row[key]}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => {
                  const status = normalizeStatus(
                    row[key] || row?.requestStatus || row?.supportStatus || ""
                  );
                  return (
                    <Badge
                      text={status ? status.toUpperCase() : "OPEN"}
                      color={status === "closed" ? "green" : "yellow"}
                    />
                  );
                },
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD MMM YYYY")}</div>
                ),
              },
              // {
              //   key: "actions",
              //   header: "Update Status",
              //   accessor: ({ row }) => {
              //     const rowId = row?._id || row?.id;
              //     const currentStatus = normalizeStatus(
              //       row?.status ||
              //         row?.requestStatus ||
              //         row?.supportStatus ||
              //         ""
              //     );
              //     const value =
              //       (rowId && statusOverrides[rowId]) || currentStatus;
              //     return (
              //       <div className="min-w-[140px]">
              //         <Dropdown
              //           value={value || ""}
              //           options={statusOptions}
              //           onChange={(val) => handleStatusChange(row, val)}
              //         />
              //       </div>
              //     );
              //   },
              // },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={(page) => setCurrentPage(page)}
          />
          {openRequestModal && (
            <Modal
              open={openRequestModal}
              onClose={handleCloseRequestModal}
              title={`Support Request #${
                selectedRequest?._id ||
                selectedRequest?.id ||
                selectedRequest?.requestId ||
                ""
              }`}
              size="lg"
            >
              <MailContent request={selectedRequest} />
            </Modal>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default SupportRequestsPage;
